import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ActivityLog } from '../types/activityLog';

export const activityLogColumns = (): ColumnDef<ActivityLog>[] => {
  return [
    {
      accessorKey: 'activityType',
      header: 'Activity Type',
      cell: ({ row }) => {
        const activityType = row.original.activityType;
        const colorMap: Record<string, string> = {
          created: 'success',
          updated: 'info',
          deleted: 'danger',
          completed: 'primary'
        };
        return (
          <Badge
            bg={colorMap[activityType] || 'secondary'}
            pill
            className="text-nowrap"
          >
            {activityType}
          </Badge>
        );
      }
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="text-nowrap">{row.original.description || 'N/A'}</span>
      )
    },
    {
      accessorKey: 'metadata',
      header: 'Changes',
      cell: ({ row }) => {
        const { activityType, metadata } = row.original;

        if (!metadata || !['updated', 'completed'].includes(activityType)) {
          return <span className="text-muted">No changes</span>;
        }

        // Handle direct subtask completion metadata (e.g., subtask completed events)
        if (
          metadata.subtaskIndex !== undefined &&
          metadata.subtaskText &&
          metadata.completed !== undefined
        ) {
          const subtaskIndex =
            (metadata.subtaskIndex as any)?.newValue || metadata.subtaskIndex;
          const subtaskText =
            (metadata.subtaskText as any)?.newValue || metadata.subtaskText;
          const completed =
            (metadata.completed as any)?.newValue || metadata.completed;

          const subtaskNumber = parseInt(subtaskIndex) + 1;
          const status = completed ? '✓ Completed' : '○ Pending';

          return (
            <div className="d-flex flex-column gap-1">
              <div className="border-bottom pb-1 mb-1">
                <div className="fw-semibold">Subtask #{subtaskNumber}:</div>
                <div className="small">
                  <span className="text-success">
                    {status}: "{subtaskText}"
                  </span>
                </div>
              </div>
            </div>
          );
        }

        const formatValue = (value: any, key?: string) => {
          if (Array.isArray(value)) {
            if (value.length === 0) return 'None';

            // Special handling for subtask arrays
            if (key === 'subTask' && value.length > 0 && value[0]?.text) {
              const subtaskDetails = value
                .map((subtask: any, index: number) => {
                  const status = subtask.completed ? '✓' : '○';
                  const completedInfo =
                    subtask.completed && subtask.completedAt
                      ? ` (Completed: ${format(
                          new Date(subtask.completedAt),
                          'MMM dd, yyyy HH:mm'
                        )})`
                      : '';
                  return `${index + 1}. ${status} ${
                    subtask.text
                  }${completedInfo}`;
                })
                .join('\n');

              const tooltipContent = (
                <Tooltip id={`subtask-tooltip-${Math.random()}`}>
                  <div style={{ textAlign: 'left', whiteSpace: 'pre-line' }}>
                    <strong>Subtasks:</strong>
                    <br />
                    {subtaskDetails}
                  </div>
                </Tooltip>
              );

              return (
                <OverlayTrigger placement="top" overlay={tooltipContent}>
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline dotted'
                    }}
                  >
                    [{value.length} items]
                  </span>
                </OverlayTrigger>
              );
            }

            return `[${value.length} items]`;
          }
          if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value);
          }
          return String(value);
        };

        return (
          <div className="d-flex flex-column gap-1">
            {Object.entries(metadata).map(([key, changes]: [string, any]) => {
              if (!changes.oldValue || !changes.newValue) return null;

              if (
                JSON.stringify(changes.oldValue) ===
                JSON.stringify(changes.newValue)
              ) {
                return null;
              }

              const oldFormatted = formatValue(changes.oldValue, key);
              const newFormatted = formatValue(changes.newValue, key);

              return (
                <div key={key} className="border-bottom pb-1 mb-1">
                  <div className="fw-semibold text-capitalize">{key}:</div>
                  <div className="small d-flex gap-2">
                    <span className="text-danger text-decoration-line-through">
                      {oldFormatted}
                    </span>
                    <span className="text-success">{newFormatted}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
    },
    {
      accessorKey: 'userId',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original?.userId;
        return (
          <span className="text-nowrap">
            {`${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
              user?.email ||
              'N/A'}
          </span>
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => {
        try {
          return (
            <span className="text-nowrap">
              {format(new Date(row.original.createdAt), 'MMM dd, yyyy hh:mm a')}
            </span>
          );
        } catch (e) {
          return <span className="text-nowrap">{row.original.createdAt}</span>;
        }
      }
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      cell: ({ row }) => {
        return (
          <span className="text-nowrap">
            {format(new Date(row.original.updatedAt), 'MMM dd, yyyy hh:mm a')}
          </span>
        );
      }
    }
  ];
};

export default activityLogColumns;
